import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const navigateToServices = (navigate: any) => {
    navigate('/#services');
  };

const AnimatedButton = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => {
    const { ref, isVisible } = useScrollAnimation();
    
    return (
        <div 
            ref={ref}
            className={`flex justify-center lg:justify-start transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
            <button 
                onClick={onClick}
                className="font-columbia-serial text-[#f4f1e9] text-xs sm:text-sm rounded-lg border border-[#231f20] px-6 sm:px-9 py-3 bg-[#231f20] hover:text-[#231f20] hover:bg-[#f4f1e9] transition border-solid">
                {children}
            </button>
        </div>
    );
};

const FAQItem = ({ question, answer }: { question: string; answer: React.ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { ref, isVisible } = useScrollAnimation();
    
    return (
        <div 
            ref={ref}
            className={`max-w-lg transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="w-full text-left flex justify-between items-center gap-4"
            >
                <h3 className="text-base md:text-lg font-semibold">{question}</h3>
                <svg 
                    className={`w-5 h-5 transition-transform duration-500 ease-in-out flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            <div 
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-[1000px] opacity-100 mt-3' : 'max-h-0 opacity-0'
                }`}
            >
                <p className="text-justify">
                    {answer}
                </p>
            </div>
        </div>
    );
};

const FAQ = () => {
    const navigate = useNavigate();

    return (
    <div id='FAQ' className="bg-[#f4f1e9] pb-24 px-0 scroll-pt-6 scroll-mt-16">
        {/* Hero Section */}
        <div className="relative h-[250px] md:h-[500px]">
            <img
                src="assets/arch-with-hanging-pampas-decor-picnic-background-datesaverco-dmv.webp"
                alt="For You Picnic"
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex w-full">
                <h1 className="text-white font-the-seasons text-lg md:text-4xl md:text-left text-center flex items-end pl-5 pb-11 md:items-end md:pl-14 md:pb-28">
                FREQUENTLY ASKED QUESTIONS
                </h1>
            </div>
        </div>    
        
        {/* FAQ Content */}
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="flex flex-col items-center lg:items-start font-columbia-serial space-y-8 text-black">
                <h2 className="text-3xl md:text-4xl font-the-seasons pt-20 self-start">YOUR QUESTIONS, <br /> ANSWERED</h2>

                <div className="text-2xl font-normal text-center w-full max-w-lg pb-3 border-b border-black/10">GENERAL BOOKING</div>

                <FAQItem 
                    question="WHICH MARKETS DO YOU SERVE?"
                    answer="We serve DC, Southern Maryland, and Northern Virginia for Picnics. And we travel worldwide for Event Content Creation. A travel fee may apply beyond 15 miles."
                />

                <FAQItem 
                    question="HOW DO I RESERVE?"
                    answer={<>Your date is secured as soon as you've paid the <span className='font-semibold'>retainer</span> and signed the <span className='font-semibold'>contract.</span> If plans change, no worries! We'll happily refund your fee if you cancel not more than 2 days from booking.</>}
                />

                <FAQItem 
                    question="WHAT IS THE RETAINER FEE FOR?"
                    answer="To reserve your special date with Date Saver Co., we require a retainer fee. This fee secures your date and allows us to plan ahead, ensuring everything runs smoothly. We're here to make your experience unforgettable, and this step helps us do just that!"
                />

                <FAQItem 
                    question="HOW FAR IN ADVANCE SHOULD I BOOK?"
                    answer="Generally, bookings must be made at least 48 hours in advance. We cannot accept bookings with less than 48 hours' notice. To secure your preferred date and time, it's best to book as early as possible, as availability may be limited."
                />

                <div className="text-2xl font-normal text-center w-full max-w-lg mt-8 pb-3 border-b border-black/10">RESCHEDULING / CANCELLATIONS</div>

                <FAQItem 
                    question="CANCELLATIONS AND RESCHEDULING"
                    answer="Please note that our retainer fee is non-refundable due to the time and effort we invest in planning and preparation. However, we're more than happy to help you reschedule your event to a later date that suits you better. Our goal is to make sure you have a memorable experience, and we're here to assist in any way we can!"
                />

                <div className="text-2xl font-normal text-center w-full max-w-lg mt-8 pb-3 border-b border-black/10">WEATHER POLICY</div>

                <FAQItem 
                    question="EVENT CONTENT CREATION"
                                        answer={
                                            <>
                                                In the event of inclement weather that poses a risk to the safety of the team, guest, or equipment, Date Saver Co reserves the right to modify, delay or pause filming. If weather prevents completion of the scheduled coverage and the client reschedules the event, the retainer will be applied to the new date, subject to availability.
                                                <br />
                                                <br />
                                                If filming continues under weather conditions that limit what can be safely or effectively captured, the client acknowledges that Date Saver Co is not responsible for any reduced or incomplete footage, and no refunds will be issued for time or coverage lost due to unsafe or unfavorable weather.
                                            </>
                                        }
                />

                <FAQItem 
                    question="WEATHER INSURANCE"
                    answer="For $95, our weather insurance lets you cancel last minute due to bad weather and get a full refund, including your retainer. The $95 fee is non-refundable."
                />
            </div>

            {/* Right Column */}
            <div className="flex flex-col items-center lg:items-start font-columbia-serial space-y-8 text-black">
                <div className='overflow-hidden rounded-[60px] pt-11'>
                    <img
                        src="/assets/faq-two-knit-chairs-with-umbrella-between-grazed-by-the-golden-hour-sunlight-date-saver-co-picnic-DMV.webp"
                        alt="Outdoor scene"
                        className="relative rounded-[30px] md:rounded-[60px] w-full md:max-w-sm max-w-xs h-auto"
                    />
                </div>

                <div className="text-2xl font-normal text-center w-full max-w-lg mt-8 pb-3 border-b border-black/10">PICNIC / LAZY BOY</div>

                <FAQItem 
                    question="HOW EARLY WILL YOU SET UP FOR MY APPOINTMENT?"
                    answer="We usually start setting up about 2 hours before your event, or a bit longer for larger guest lists. To ensure everything is perfectly ready and you get the full Date Saver Co. experience, we kindly ask that you arrive closer to the scheduled time rather than early."
                />

                <FAQItem 
                    question="HOW MANY GUESTS CAN YOU ACCOMODATE AT THE TABLE?"
                    answer="Right now, we can accommodate up to 4 guests for our picnics and Lazy Boy high tables. As we continue to grow, we plan to expand this number to host more guests."
                />

                <AnimatedButton onClick={() => navigateToServices(navigate)}>
                    BOOK NOW
                </AnimatedButton>

                <div className="text-2xl font-normal text-center w-full max-w-lg mt-8 pb-3 border-b border-black/10">EVENT CONTENT CREATOR</div>

                <FAQItem 
                    question="WHAT IS AN EVENT CONTENT CREATOR?"
                    answer={<>Introducing a NEW way to capture your event, where people show up as themselves, not the version they feel expected to be, as if it was filmed from a guest's perspective. A professional DMV-based content creator will record your event using an <span className='font-semibold'>iPhone</span>. You'll receive both unedited footage and edited videos in "short-style" format, perfect for easy viewing and sharing. For more details about our content creator, feel free to message us!</>}
                />

                <FAQItem 
                    question="HOW WILL THE CONTENT BE RECORDED?"
                    answer={<>We'll use an <span className='font-semibold'>iPhone</span> to capture your event, along with a <span className='font-semibold'>separate microphone</span> for high-quality audio. Filming this on a phone aims to focus on natural, candid stories that truly reflect the essence of your experience.</>}
                />

                <FAQItem 
                    question="CAN I REQUEST SPECIFIC SHOTS TO BE CAPTURED?"
                    answer="Absolutely! You can list all your shot requests in the intake form. We'll review your details and get back to you with a quote."
                />

                <FAQItem 
                    question="WHEN WILL I RECEIVE THE CONTENT AFTER THE EVENT?"
                    answer="You'll receive your content within 1-2 weeks after the event, depending on add-ons and the event's length."
                />

                <FAQItem 
                    question="CAN I USE THE CONTENT ON SOCIAL MEDIA?"
                    answer="Absolutely! We'd appreciate it if you could tag both the content creator and Date Saver Co. in your posts. It helps us share the great moments and support our work. Thanks so much!"
                />

                <FAQItem 
                    question="IS THERE AN OPTION FOR ADDITIONAL EDITS?"
                    answer="Yes, if you have specific edits or inspiration in mind, let us know! We'll work with the content creator to see what can be accommodated based on their availability and agreement."
                />

                <FAQItem 
                    question="HOW TO BOOK AN EVENT CONTENT CREATOR:"
                    answer={
                        <div className="space-y-3">
                            <div>
                                <h2 className="font-medium">1. Submit Inquiry</h2>
                                <p>Fill out the <a href="https://datesaverco.com/contentcreator" className="underline hover:text-[#7a6d4e]">inquiry form</a>, tell us more about your event and we will get back to you via email.</p>
                            </div>
                            <div>
                                <h2 className="font-medium">2. Call</h2>
                                <p>Join a brief consult call to go over your event details.
                                    After the call, review and complete the Client Agreement Form and Retainer Invoice to officially book your appointment.</p>
                            </div>
                            <div>
                                <h2 className="font-medium">3. Date Saved</h2>
                                <p>Once the Retainer Invoice is completed, and the Client Agreement Form signed, you are all set. 
                                    Your remaining balance and add-ons will auto-charge 48 hours before your event. Now unplug and enjoy...no phones, no worries.</p>
                            </div>
                        </div>
                    }
                />

                <AnimatedButton onClick={() => navigateToServices(navigate)}>
                    BOOK NOW
                </AnimatedButton>
            </div>        
        </div>
    </div>
    );
}

export default FAQ;
